// src/features/payment/PaymentMethodsCard.tsx
// Interactive payment-methods management for a billing account.
// Embedded in BillingAccountDetailPage — "attach, don't duplicate": billing
// already shows a read-only payment_methods snapshot on the account record,
// this component adds the real actions backed by PaymentService.
//
// Covers: ListPaymentMethods, CreatePaymentMethod, DeletePaymentMethod,
// SetDefaultPaymentMethod, GetPaymentMethod, UpdateBankRequisites,
// AttachPaymentMethodToLegalEntity, DetachPaymentMethodFromLegalEntity.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { usePaymentPaymentMethod, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { PaymentMethodDetail } from '@core/types/payment';
import { humanizeEnum } from './format';
import { AddPaymentMethodModal } from './AddPaymentMethodModal';
import { BankRequisitesModal } from './BankRequisitesModal';

export interface PaymentMethodsCardProps {
  billingAccountId: string;
}

function methodLabel(pm: PaymentMethodDetail): string {
  if (pm.card_brand) return `${pm.card_brand.toUpperCase()} •••• ${pm.card_last_four}`;
  if (pm.bank_name) return `${pm.bank_name} •••• ${pm.bank_account_last_four}`;
  return humanizeEnum(pm.type, 'PAYMENT_METHOD_TYPE_') || '--';
}

export const PaymentMethodsCard = observer(({ billingAccountId }: PaymentMethodsCardProps) => {
  const store = usePaymentPaymentMethod();
  const { t } = useI18n();
  const [addOpen, setAddOpen] = useState(false);
  const [manageTarget, setManageTarget] = useState<PaymentMethodDetail | null>(null);
  const [removeTarget, setRemoveTarget] = useState<PaymentMethodDetail | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (billingAccountId) store.ListPaymentMethods(billingAccountId).catch(() => {});
  }, [billingAccountId]);

  const handleAdd = async (data: { type: string; provider_code: string; provider_token: string; set_as_default: boolean }) => {
    await store.CreatePaymentMethod(billingAccountId, {
      entity: {} as any,
      billing_account: { id: billingAccountId } as any,
      type: data.type as any,
      provider_code: data.provider_code,
      provider_token: data.provider_token,
      set_as_default: data.set_as_default,
      metadata: {},
    } as any);
    setAddOpen(false);
  };

  const handleSetDefault = async (pm: PaymentMethodDetail) => {
    if (!pm.entity?.id) return;
    setBusyId(pm.entity.id);
    try {
      await store.SetDefaultPaymentMethod(billingAccountId, pm.entity.id, {} as any);
      await store.ListPaymentMethods(billingAccountId);
    } catch {
      // surfaced via store.error
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget?.entity?.id) return;
    setBusyId(removeTarget.entity.id);
    try {
      await store.DeletePaymentMethod(billingAccountId, removeTarget.entity.id);
      setRemoveTarget(null);
    } catch {
      // surfaced via store.error
    } finally {
      setBusyId(null);
    }
  };

  const openManage = async (pm: PaymentMethodDetail) => {
    if (pm.entity?.id) {
      try {
        const full = await store.GetPaymentMethod(pm.entity.id);
        setManageTarget(full);
        return;
      } catch {
        // fall through to cached record
      }
    }
    setManageTarget(pm);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>{t('payment.methods.title')}</CardTitle>
        <Button size="sm" onClick={() => setAddOpen(true)}>{t('payment.methods.add')}</Button>
      </CardHeader>
      <CardContent>
        {store.error ? (
          <div className="mb-4"><ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /></div>
        ) : null}

        {store.isLoading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : store.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('payment.methods.empty')}</p>
        ) : (
          <Table striped hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>{t('payment.methods.type')}</TableHead>
                <TableHead>{t('payment.methods.details')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead>{t('payment.methods.default')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {store.items.length === 0 ? (
                <TableEmpty colSpan={5} message={t('payment.methods.empty')} />
              ) : (
                store.items.map((pm) => (
                  <TableRow key={pm.entity?.id}>
                    <TableCell>{humanizeEnum(pm.type, 'PAYMENT_METHOD_TYPE_')}</TableCell>
                    <TableCell>{methodLabel(pm)}</TableCell>
                    <TableCell>
                      {pm.is_valid ? (
                        <Badge variant="success" size="sm">{t('payment.common.valid')}</Badge>
                      ) : (
                        <Badge variant="error" size="sm">{pm.failure_reason || t('payment.common.invalid')}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {pm.is_default ? (
                        <Badge variant="primary" size="sm">{t('payment.methods.default')}</Badge>
                      ) : (
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline disabled:opacity-50"
                          disabled={busyId === pm.entity?.id}
                          onClick={() => handleSetDefault(pm)}
                        >
                          {t('payment.methods.setAsDefault')}
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => openManage(pm)}>{t('payment.methods.manage')}</Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={pm.is_default || busyId === pm.entity?.id}
                          onClick={() => setRemoveTarget(pm)}
                          title={pm.is_default ? t('payment.methods.cannotRemoveDefault') : undefined}
                        >
                          {t('common.remove')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <AddPaymentMethodModal
        open={addOpen}
        isSaving={store.isSaving}
        onCancel={() => setAddOpen(false)}
        onSubmit={handleAdd}
      />

      <BankRequisitesModal
        open={!!manageTarget}
        method={manageTarget}
        isSaving={store.isSaving || store.isLoading}
        onCancel={() => setManageTarget(null)}
        onSaveRequisites={async (bankRequisites) => {
          if (!manageTarget?.entity?.id) return;
          const updated = await store.UpdateBankRequisites(manageTarget.entity.id, {
            payment_method: manageTarget.entity as any,
            field_mask: [],
            bank_requisites: bankRequisites as any,
          } as any);
          setManageTarget(updated);
          await store.ListPaymentMethods(billingAccountId);
        }}
        onAttachLegalEntity={async (legalEntityId) => {
          if (!manageTarget?.entity?.id) return;
          await store.AttachPaymentMethodToLegalEntity(manageTarget.entity.id, {
            payment_method: manageTarget.entity as any,
            legal_entity: { id: legalEntityId } as any,
          } as any);
          // Attach returns a bare Response, not the updated record — reflect
          // the change optimistically rather than refetch (GetPaymentMethod
          // serves from the store's own cache, which this call does not bust).
          setManageTarget({ ...manageTarget, legal_entity: { id: legalEntityId } as any });
        }}
        onDetachLegalEntity={async () => {
          if (!manageTarget?.entity?.id) return;
          await store.DetachPaymentMethodFromLegalEntity(manageTarget.entity.id);
          setManageTarget({ ...manageTarget, legal_entity: undefined });
        }}
      />

      <ConfirmDialog
        open={!!removeTarget}
        title={t('payment.methods.removeConfirmTitle')}
        description={t('payment.methods.removeConfirmDesc')}
        destructive
        isConfirming={busyId === removeTarget?.entity?.id}
        onConfirm={handleRemove}
        onCancel={() => setRemoveTarget(null)}
      />
    </Card>
  );
});

export default PaymentMethodsCard;
